"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  Info,
  ArrowDownToLine,
  ArrowUpFromLine,
  Search,
  Eye,
  X,
  Loader2,
  CheckCheck,
  Trash2,
  Filter,
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  getMyNotifications,
  markNotificationRead,
  removeNotification as apiRemoveNotification,
} from "@/shared/api/notifications";

// ── Type config (UPPERCASE keys — matches API type values) ──────────────
const TYPE_CONFIG = {
  SUCCESS: {
    icon: CheckCircle2,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    badge: "bg-emerald-500/15 text-emerald-400",
    label: "Success",
    labelKey: "success",
  },
  WITHDRAWAL: {
    icon: ArrowUpFromLine,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    badge: "bg-emerald-500/15 text-emerald-400",
    label: "Withdrawal",
    labelKey: "withdrawal",
  },
  DEPOSIT: {
    icon: ArrowDownToLine,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    badge: "bg-blue-500/15 text-blue-400",
    label: "Deposit",
    labelKey: "deposit",
  },
  SYSTEM: {
    icon: Info,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    badge: "bg-blue-500/15 text-blue-400",
    label: "System",
    labelKey: "system",
  },
};

const DEFAULT_CONFIG = {
  icon: Info,
  color: "text-gray-400",
  bg: "bg-gray-500/10",
  border: "border-gray-500/30",
  badge: "bg-gray-500/15 text-gray-400",
  label: "Other",
  labelKey: "other",
};

function mapFromApi(n) {
  return {
    id: n.id,
    title: n.title,
    message: n.message,
    type: n.type,
    read: n.isRead,
    time: new Date(n.createdAt),
  };
}

// ── Translation helpers ─────────────────────────────────────────────────
const titleMap = {
  "New Withdrawal": "newWithdrawal",
  "Withdrawal Approved": "withdrawalApproved",
  "Withdrawal Rejected": "withdrawalRejected",
  "New Deposit": "newDeposit",
  "Deposit Confirmed": "depositConfirmed",
  Welcome: "welcome",
};

const messagePatterns = [
  {
    regex: /^(.+?) requested a withdrawal of (\w+) (.+)$/,
    key: "withdrawalRequestedMessage",
    groups: ["name", "currency", "amount"],
  },
  {
    regex: /^Withdrawal of (\w+) (.+) has been approved$/,
    key: "withdrawalApprovedMessage",
    groups: ["currency", "amount"],
  },
  {
    regex: /^Withdrawal of (\w+) (.+) has been rejected$/,
    key: "withdrawalRejectedMessage",
    groups: ["currency", "amount"],
  },
  {
    regex: /^New deposit of (\w+) (.+) received$/,
    key: "newDepositMessage",
    groups: ["currency", "amount"],
  },
  {
    regex: /^Welcome back, (.+)!$/,
    key: "welcomeBackUserMessage",
    groups: ["name"],
  },
];

function translateTitle(title, t) {
  const key = titleMap[title];
  if (key) {
    const translated = t(key);
    return translated !== key ? translated : title;
  }
  return title;
}

function translateMessage(message, t) {
  for (const pattern of messagePatterns) {
    const match = message.match(pattern.regex);
    if (match) {
      const translated = t(pattern.key);
      if (translated !== pattern.key) {
        let result = translated;
        pattern.groups.forEach((g, i) => {
          result = result.replace(`{${g}}`, match[i + 1]);
        });
        return result;
      }
    }
  }
  return message;
}

// ── Main page component ─────────────────────────────────────────────────
export default function Notifications() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // ── State ─────────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "unread" | "read"
  const [selectedType, setSelectedType] = useState(null);   // null = all types

  // Bulk action states
  const [markingAll, setMarkingAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getMyNotifications();
      setNotifications(list.map(mapFromApi));
    } catch (err) {
      setError(err?.message || "Failed to load notifications");
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ── Statistics (computed from full unfiltered list) ────────────────────
  const stats = useMemo(() => {
    const total = notifications.length;
    const unread = notifications.filter((n) => !n.read).length;
    const read = notifications.filter((n) => n.read).length;
    return { total, unread, read };
  }, [notifications]);

  // ── Available type buttons (only types that exist in data) ────────────
  const availableTypes = useMemo(() => {
    return Array.from(new Set(notifications.map((n) => n.type)));
  }, [notifications]);

  // ── Client-side filtering ─────────────────────────────────────────────
  const filteredNotifications = useMemo(() => {
    return notifications.filter((n) => {
      // Status filter
      if (statusFilter === "unread" && n.read) return false;
      if (statusFilter === "read" && !n.read) return false;

      // Type filter
      if (selectedType && n.type !== selectedType) return false;

      // Search filter
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        if (
          !n.title.toLowerCase().includes(term) &&
          !n.message.toLowerCase().includes(term)
        )
          return false;
      }

      return true;
    });
  }, [notifications, statusFilter, selectedType, searchTerm]);

  const hasFiltersActive =
    statusFilter !== "all" || selectedType !== null || searchTerm.trim() !== "";

  // ── Actions ───────────────────────────────────────────────────────────
  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
      toast.success("Notification marked as read");
    } catch {
      toast.error("Failed to mark notification as read");
    }
  };

  const handleRemove = async (id) => {
    try {
      const ok = await apiRemoveNotification(id);
      if (ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
        toast.success("Notification removed");
      } else {
        toast.error("Failed to remove notification");
      }
    } catch {
      toast.error("Failed to remove notification");
    }
  };

  const handleMarkAllRead = async () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;

    setMarkingAll(true);
    try {
      const results = await Promise.allSettled(
        unreadIds.map((id) => markNotificationRead(id))
      );

      const successIds = new Set();
      results.forEach((r, i) => {
        if (r.status === "fulfilled") successIds.add(unreadIds[i]);
      });

      setNotifications((prev) =>
        prev.map((n) => (successIds.has(n.id) ? { ...n, read: true } : n))
      );
      queryClient.invalidateQueries({ queryKey: ["myNotifications"] });

      if (successIds.size === unreadIds.length) {
        toast.success("All notifications marked as read");
      } else {
        toast.error(
          `Marked ${successIds.size}/${unreadIds.length} as read — some failed`
        );
      }
    } catch {
      toast.error("Failed to mark all as read");
    } finally {
      setMarkingAll(false);
    }
  };

  const handleClearAll = async () => {
    if (notifications.length === 0) return;

    setClearingAll(true);
    try {
      const allIds = notifications.map((n) => n.id);
      const results = await Promise.allSettled(
        allIds.map((id) => apiRemoveNotification(id))
      );

      const removedIds = new Set();
      results.forEach((r, i) => {
        if (r.status === "fulfilled" && r.value) removedIds.add(allIds[i]);
      });

      setNotifications((prev) =>
        prev.filter((n) => !removedIds.has(n.id))
      );
      queryClient.invalidateQueries({ queryKey: ["myNotifications"] });

      if (removedIds.size === allIds.length) {
        toast.success("All notifications cleared");
      } else {
        toast.error(
          `Cleared ${removedIds.size}/${allIds.length} — some failed`
        );
      }
    } catch {
      toast.error("Failed to clear notifications");
    } finally {
      setClearingAll(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#0d1321] to-[#0a0e1a] p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* ── Page Header ──────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Bell className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {t("notifications")}
              </h1>
              <p className="text-sm text-gray-400">
                {t("manageYourNotifications")}
              </p>
            </div>
          </div>

          {/* Bulk actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllRead}
              disabled={markingAll || clearingAll || stats.unread === 0}
              className="bg-transparent border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300 disabled:opacity-40"
            >
              {markingAll ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <CheckCheck className="h-4 w-4 mr-1" />
              )}
              {t("markAllRead")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              disabled={clearingAll || markingAll || stats.total === 0}
              className="bg-transparent border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40"
            >
              {clearingAll ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
              {t("clearAll") || "Clear All"}
            </Button>
          </div>
        </div>

        {/* ── Statistics Bar ───────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#0f172a]/80 border border-cyan-500/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-gray-400 mt-1">{t("total") || "Total"}</p>
          </div>
          <div className="bg-[#0f172a]/80 border border-cyan-500/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-cyan-400">{stats.unread}</p>
            <p className="text-xs text-gray-400 mt-1">{t("unread") || "Unread"}</p>
          </div>
          <div className="bg-[#0f172a]/80 border border-cyan-500/10 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-gray-400">{stats.read}</p>
            <p className="text-xs text-gray-400 mt-1">{t("read") || "Read"}</p>
          </div>
        </div>

        {/* ── Filters Bar ──────────────────────────────────────────────── */}
        <div className="bg-[#0f172a]/80 border border-cyan-500/10 rounded-xl p-4 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder={t("searchNotifications")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-[#0b1220] border-cyan-500/10 text-gray-200 placeholder:text-gray-500 focus:border-cyan-500/30 focus:ring-cyan-500/20"
            />
          </div>

          {/* Status + Type filters */}
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500 mr-1" />

            {/* Status buttons */}
            {["all", "unread", "read"].map((s) => (
              <Button
                key={s}
                variant="ghost"
                size="sm"
                onClick={() => setStatusFilter(s)}
                className={`text-xs capitalize ${statusFilter === s
                  ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                  }`}
              >
                {t(s) || s.charAt(0).toUpperCase() + s.slice(1)}
                {s === "unread" && stats.unread > 0 && (
                  <span className="ml-1 text-[10px] bg-cyan-500/20 text-cyan-400 px-1.5 rounded-full">
                    {stats.unread}
                  </span>
                )}
              </Button>
            ))}

            {/* Separator */}
            <div className="w-px h-5 bg-cyan-500/10" />

            {/* Type filter buttons */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedType(null)}
              className={`text-xs ${selectedType === null
                ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                }`}
            >
              {t("allTypes") || "All Types"}
            </Button>
            {availableTypes.map((type) => {
              const config = TYPE_CONFIG[type] || DEFAULT_CONFIG;
              return (
                <Button
                  key={type}
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedType(type)}
                  className={`text-xs ${selectedType === type
                    ? `${config.bg} ${config.color} border ${config.border}`
                    : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                    }`}
                >
                  {t(config.labelKey) || config.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* ── Error banner ─────────────────────────────────────────────── */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2 text-red-400 text-sm">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* ── Notification List ────────────────────────────────────────── */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400 mb-3" />
              <p className="text-sm">{t("loading")}</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <AlertTriangle className="h-10 w-10 mb-3 text-yellow-500/50" />
              <p className="text-sm font-medium">
                {hasFiltersActive
                  ? t("noNotificationsMatchingFilters")
                  : t("noNotificationsYet")}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => {
              const config =
                TYPE_CONFIG[notification.type] || DEFAULT_CONFIG;
              const Icon = config.icon;

              return (
                <div
                  key={notification.id}
                  className={`group rounded-xl border p-4 transition-all duration-200 hover:shadow-lg hover:shadow-cyan-500/5 ${!notification.read
                    ? "bg-blue-500/5 border-blue-500/20 hover:border-blue-500/30"
                    : "bg-[#0f172a]/60 border-slate-700/30 hover:border-slate-600/40"
                    }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Left icon */}
                    <div
                      className={`w-10 h-10 rounded-lg ${config.bg} border ${config.border} flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon className={`h-5 w-5 ${config.color}`} />
                    </div>

                    {/* Center content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p
                          className={`text-sm font-semibold ${!notification.read ? "text-white" : "text-gray-300"
                            }`}
                        >
                          {translateTitle(notification.title, t)}
                        </p>
                        <Badge
                          variant="secondary"
                          className={`text-[10px] px-1.5 py-0 ${config.badge} border-0`}
                        >
                          {t(config.labelKey) || config.label}
                        </Badge>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-cyan-400 rounded-full shadow-[0_0_6px_rgba(0,255,255,0.8)]" />
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                        {translateMessage(notification.message, t)}
                      </p>
                      <p className="text-[10px] text-gray-500 mt-1.5">
                        {format(notification.time, "MMM dd, yyyy h:mm a")}
                      </p>
                    </div>

                    {/* Right actions */}
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!notification.read && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          title="Mark as read"
                          className="p-1.5 rounded-lg text-gray-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleRemove(notification.id)}
                        title="Remove"
                        className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
